/**
 * @file        utils/http.js            Helper module for things related to HTTP or HTTPS networking.
 * @author      Wes Garland, wes@kingsds.network
 * @date        Apr 2020
 */
'use strict';

/**
 * A class for creating "server-side" errors that instanceof Error but which originate on "the server"
 * when we are "the client".
 */
class ServerSideError extends Error
{
  /**
   * @param {Error}  unsafeErr        an Error-like object received from a potentially untrustworthy source
   * @param {string} endpoint         the origin of the error (used for stack annotation)
   * @param {string} localStackLabel  how to label the part of the stack that was generated in this process
   */
  constructor(unsafeErr, endpoint, localStackLabel)
  {
    const err = {
      stack:   'ServerSide ' + unsafeErr.stack,
      message: unsafeErr.message,
    };
    if (typeof unsafeErr.lineNumber !== 'undefined')
    {
      err.lineNumber   = unsafeErr.lineNumber;
      err.fileName     = unsafeErr.fileName;
      err.columnNumber = unsafeErr.columnNumber;
    }

    if (unsafeErr.cause)
      err.cause = unsafeErr.cause;
    if (unsafeErr.code)
      err.code = unsafeErr.code;

    super(err.message);
    const stackDivider = `\n----- ${localStackLabel || 'local stack'} -----\n`;
    const localStack = this.stack;

    if (this.lineNumber === undefined) /* assume v8 */
    {
      this.stack = err.stack
        + '\n    at ' + endpoint.replace(/:([^/])/,' port $1')
        + stackDivider
        + localStack.split('\n').slice(1).join('\n');
    }
    else /* transform v8 stack from server to look like a spidermonkey stack */
    {
      let stack = err.stack.split('\n');
      stack = stack.map(el => el.replace(/(^ *at )([^)]*)\(([^)]*)(\))/,'$2@file://$3'));
      this.stack = stack.join('\n')
        + '\n@' + endpoint.replace(/:([^/])/,' port $1') + ':0:0\n'
        + stackDivider
        + localStack;
    }
  }
  get name()
  {
    return 'ServerSideError';
  }
}
exports.ServerSideError = ServerSideError;
